import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { api } from "../api";

const KRONOS_CONTEXT = `You are Kronos Assistant, the helpful AI guide for Kronos AI platform. You have complete knowledge of the software:

## About Kronos AI
Kronos AI is an intelligent command center for freelancers and businesses, built by DevX House. It combines AI-powered automation for social media, proposals, documents, and customer service.

## Features You Know About:

### 1. Dashboard
- Overview of all activities and quick stats
- Recent posts, proposals, and usage metrics
- Quick access to all features

### 2. AI Receptionist
- 24/7 AI-powered phone answering
- Supports 50+ languages including Urdu, Hindi, Arabic
- Learns from your business website automatically
- Handles appointments, FAQs, and customer inquiries
- Premium voices available (natural-sounding)
- Setup: Just paste your website URL and configure

### 3. Social Media
- AI generates posts for Twitter, LinkedIn, Facebook, Instagram
- Schedule posts for optimal times
- Generate images with AI
- Connect multiple social accounts
- Analytics and performance tracking

### 4. Proposals
- AI generates personalized freelance proposals
- Searches jobs from 14+ platforms (Upwork, Freelancer, Fiverr, etc.)
- Multiple profile support - create different profiles for different skills
- Customize tone (professional, friendly, confident, consultative)
- Customize length (concise, standard, detailed)
- Uses your freelancer profile to personalize proposals

### 5. Fable AI (Document Generator)
- Create business documents with AI
- Templates: Business Plans, Pitch Decks, Marketing Strategies, Proposals, SOPs, etc.
- Website analysis to auto-fill company info
- Export to PDF
- Professional formatting

### 6. Pricing Plans
- Free: 5 posts/month, 3 proposals, basic features
- Professional ($79/mo): 50 posts, unlimited proposals, 3 social accounts
- Business ($199/mo): 200 posts, 10 accounts, team collaboration
- Enterprise ($499/mo): Unlimited everything, priority support

### 7. Profile Management
- Create freelancer profiles for proposal generation
- Upload CV (PDF/PNG) - AI extracts all your info automatically
- Multiple profiles for different skills/niches
- Set active profile for proposals

## Common Questions:

Q: How do I connect social media?
A: Go to Social Media tab → Click "Connect Account" → Choose platform → Authorize

Q: Why is my proposal short?
A: Select "Standard" or "Detailed" length in the Customize Proposal section

Q: How do I set up AI Receptionist?
A: Go to AI Receptionist → Paste your website URL → Click Analyze → Configure settings → Get your phone number

Q: Can I use Kronos from Pakistan?
A: Yes! Kronos uses Paddle for payments which works worldwide including Pakistan

Q: How do I upload my CV?
A: Go to Proposals → Click "Edit Profile" → Upload CV (PDF or image) → AI extracts everything

## Your Personality:
- Helpful and friendly
- Concise but complete answers
- Use bullet points for clarity
- If you don't know something specific, suggest contacting kronos@devxhouse.com
- Never make up features that don't exist
- Guide users step-by-step when explaining how to do something`;

export default function KronosHelper() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hey! I'm Kronos Helper. Ask me anything about using Kronos AI - features, how-tos, troubleshooting, or pricing. How can I help?" }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const { data } = await api.post("/kronos-helper", {
        message: userMessage,
        context: KRONOS_CONTEXT,
        history: messages.slice(-6)
      });

      setMessages(prev => [...prev, {
        role: "assistant",
        content: data.response || "Sorry, I couldn't process that. Try again or email kronos@devxhouse.com for support."
      }]);
    } catch (err) {
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "I'm having trouble connecting right now. For immediate help, email kronos@devxhouse.com"
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const quickQuestions = [
    "How do I get started?",
    "Set up AI Receptionist",
    "Generate a proposal",
    "Pricing plans"
  ];

  if (typeof document === 'undefined') return null;

  return createPortal(
    <>
      <style>{`
        @keyframes subtleGlow {
          0%, 100% { box-shadow: 0 0 20px rgba(6,182,212,0.3), 0 0 40px rgba(6,182,212,0.1); }
          50% { box-shadow: 0 0 25px rgba(6,182,212,0.5), 0 0 50px rgba(6,182,212,0.2); }
        }
        @keyframes breathe {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
      `}</style>

      {/* Chat Window */}
      {isOpen && (
        <div className="w-[calc(100vw-2rem)] sm:w-[380px] max-w-[380px] h-[70vh] sm:h-[500px] max-h-[500px] rounded-2xl flex flex-col overflow-hidden"
          style={{
            position: "fixed",
            bottom: "80px",
            right: "16px",
            background: "#000000",
            border: "1px solid rgba(255,255,255,0.08)",
            boxShadow: "0 25px 50px rgba(0,0,0,0.8)",
            zIndex: 2147483646,
            willChange: "transform",
            transform: "translateZ(0)",
            isolation: "isolate"
          }}>

          {/* Header - Clean dark */}
          <div className="px-4 py-3 flex items-center gap-3"
            style={{
              background: "#0a0a0a",
              borderBottom: "1px solid rgba(255,255,255,0.06)"
            }}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.1)"
              }}>
              <svg className="w-5 h-5 text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="8" r="4"/>
                <path d="M4 20c0-4 4-6 8-6s8 2 8 6"/>
                <path d="M8 3l-1-2M16 3l1-2" strokeLinecap="round" strokeWidth="2"/>
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-white font-medium text-sm">Kronos Helper</h3>
              <p className="text-[11px] text-gray-500">AI Assistant</p>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors hover:bg-white/5"
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Messages - Pure black background */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ background: "#000000" }}>
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                {msg.role === "assistant" && (
                  <div className="w-6 h-6 rounded-lg mr-2 flex-shrink-0 flex items-center justify-center mt-1"
                    style={{ background: "rgba(255,255,255,0.05)" }}>
                    <svg className="w-3.5 h-3.5 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <circle cx="12" cy="8" r="3"/>
                      <path d="M6 18c0-3 3-5 6-5s6 2 6 5"/>
                    </svg>
                  </div>
                )}
                <div className={`max-w-[80%] px-4 py-2.5 text-sm ${
                  msg.role === "user"
                    ? "rounded-2xl rounded-br-sm"
                    : "rounded-2xl rounded-bl-sm"
                }`}
                style={msg.role === "user" ? {
                  background: "rgba(255,255,255,0.1)",
                  color: "#ffffff"
                } : {
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.06)",
                  color: "#e5e5e5"
                }}>
                  <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="w-6 h-6 rounded-lg mr-2 flex-shrink-0 flex items-center justify-center"
                  style={{ background: "rgba(255,255,255,0.05)" }}>
                  <svg className="w-3.5 h-3.5 text-gray-400 animate-pulse" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <circle cx="12" cy="8" r="3"/>
                    <path d="M6 18c0-3 3-5 6-5s6 2 6 5"/>
                  </svg>
                </div>
                <div className="px-4 py-3 rounded-2xl rounded-bl-sm"
                  style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
                  <div className="flex gap-1.5 items-center">
                    <span className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></span>
                    <span className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></span>
                    <span className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Questions */}
          {messages.length <= 2 && (
            <div className="px-4 pb-2" style={{ background: "#000000" }}>
              <p className="text-[10px] text-gray-600 mb-2 uppercase tracking-wider">Suggested</p>
              <div className="flex flex-wrap gap-1.5">
                {quickQuestions.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => { setInput(q); inputRef.current?.focus(); }}
                    className="px-3 py-1.5 text-xs rounded-lg text-gray-400 transition-all hover:text-white hover:bg-white/5"
                    style={{
                      background: "rgba(255,255,255,0.03)",
                      border: "1px solid rgba(255,255,255,0.06)"
                    }}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input - Minimal dark */}
          <div className="p-3" style={{ background: "#0a0a0a", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask about Kronos..."
                className="flex-1 px-4 py-2.5 rounded-xl text-white text-sm placeholder:text-gray-600 focus:outline-none transition-all"
                style={{
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.08)"
                }}
                disabled={loading}
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || loading}
                className="w-10 h-10 rounded-xl flex items-center justify-center transition-all disabled:opacity-30"
                style={{
                  background: input.trim() ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.08)"
                }}
              >
                <svg className="w-5 h-5 text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3 21l18-9L3 3l3 9m0 0h12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300"
        style={{
          position: "fixed",
          bottom: "16px",
          right: "16px",
          background: isOpen ? "rgba(255,255,255,0.05)" : "#0a0a0a",
          border: "1px solid rgba(255,255,255,0.1)",
          animation: isOpen ? "none" : "subtleGlow 3s ease-in-out infinite",
          zIndex: 2147483647,
        }}
        title="Kronos Helper"
      >
        {isOpen ? (
          <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <div className="relative">
            <svg className="w-7 h-7 text-cyan-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
              style={{ animation: "breathe 3s ease-in-out infinite" }}>
              <rect x="6" y="4" width="12" height="10" rx="2" />
              <circle cx="9.5" cy="9" r="1.5" fill="currentColor" />
              <circle cx="14.5" cy="9" r="1.5" fill="currentColor" />
              <path d="M12 4V2" strokeWidth="2" strokeLinecap="round" />
              <circle cx="12" cy="1.5" r="1" fill="currentColor" stroke="none" />
              <path d="M8 14v2a2 2 0 002 2h4a2 2 0 002-2v-2" />
            </svg>
            <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-500 rounded-full"
              style={{ boxShadow: "0 0 8px rgba(16,185,129,0.6)", animation: "breathe 2s ease-in-out infinite" }}></span>
          </div>
        )}
      </button>
    </>,
    document.body
  );
}
